"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type ClockContextValue = {
  now: Date | null;
};

const ClockContext = createContext<ClockContextValue | null>(null);

export type ClockProviderProps = {
  children: React.ReactNode;
};

// Defers `new Date()` to the effect — cacheComponents treats wall-clock reads
// during render as dynamic, which would force the entire tree into Suspense.
export function ClockProvider({ children }: ClockProviderProps) {
  const [now, setNow] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setNow(new Date());
    const schedule = () => {
      const msToNextMinute = 60_000 - (Date.now() % 60_000);
      timeoutRef.current = setTimeout(() => {
        setNow(new Date());
        intervalRef.current = setInterval(() => {
          setNow(new Date());
        }, 60_000);
      }, msToNextMinute);
    };
    schedule();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const value = useMemo<ClockContextValue>(() => ({ now }), [now]);
  return (
    <ClockContext.Provider value={value}>{children}</ClockContext.Provider>
  );
}

export function useClockNow(): Date | null {
  const ctx = useContext(ClockContext);
  return ctx?.now ?? null;
}
